// The inspector for sketches: edit the selected entity, property or relationship, or, with nothing
// selected, the sketch itself with its changes, review findings and problems.
import {
  addProperty,
  addRelationship,
  diffModels,
  EditError,
  lintModel,
  makeOwned,
  removeEntity,
  removeProperty,
  removeRelationship,
  renameEntity,
  renameProperty,
  setKey,
  specAt,
  typeLabel,
  updateEntity,
  updateProperty,
  updateRelationship,
  validateDocument,
  type DeleteBehavior,
  type EfModelDocument,
  type EntitySpec,
  type ModelSpec,
  type OwnedSpec,
  type PropertySpec,
} from '@modelviewer/core';
import { ArrowLeft, ArrowRight, Bot, Braces, ChevronRight, Columns2, Copy, Download, KeyRound, Link2, Package, Plus, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { openCopyForAgent } from '../agent/CopyForAgent';
import { ChangeList } from '../diff/ChangeList';
import { MentionedIn } from '../notes/NoteInspector';
import { cn } from '../lib/cn';
import type { SketchRecord } from '../lib/db';
import { closePanel, openCompare, openDiagram, openSketch, openSourceFile } from '../state/dock';
import { baseLabel, baseRefOf, deleteSketch, duplicateSketch, editSketch, getSketch, moveLayoutEntry } from '../state/sketches';
import { toast } from '../state/toast';
import { useModel } from '../state/useModel';
import { revealEntity, select, sketchRef, type Selection } from '../state/workspace';
import { exportSketch } from './files';
import { ActionButton, Check, NumberInput, Row, Select, TextArea, TextInput, TypeOptions } from './fields';
import { useNameFocus } from './SketchView';

/** Runs an edit without applying it; shows why when it cannot be made. */
function attempt(id: string, change: (doc: EfModelDocument) => EfModelDocument): EfModelDocument | null {
  const sketch = getSketch(id);
  if (!sketch) return null;
  try {
    return change(sketch.doc);
  } catch (e) {
    if (e instanceof EditError) {
      toast(e.message, 'error');
      return null;
    }
    throw e;
  }
}

function Section({ title, children, count, defaultOpen = true }: { title: string; children: React.ReactNode; count?: number; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-border">
      <button onClick={() => setOpen(!open)} className="flex h-8 w-full items-center gap-1 px-3 text-[11px] font-semibold tracking-wider text-faint uppercase hover:text-muted" aria-expanded={open}>
        <ChevronRight size={12} className={cn('transition-transform', open && 'rotate-90')} />
        {title}
        {count !== undefined && <span className="ml-auto font-normal tracking-normal normal-case">{count}</span>}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </section>
  );
}

function Header({ kind, children }: { kind: string; children: React.ReactNode }) {
  return (
    <header className="border-b border-border px-3 py-3">
      <p className="mb-1 text-[11px] font-semibold tracking-wider text-faint uppercase">{kind}</p>
      {children}
    </header>
  );
}

function useBaseModel(sketch: SketchRecord): ModelSpec | undefined {
  const baseRef = useMemo(() => baseRefOf(sketch), [sketch]);
  return useModel(baseRef).model?.result.doc.model;
}

const DELETE_OPTIONS: { value: DeleteBehavior; label: string }[] = [
  { value: 'cascade', label: 'Cascade: delete dependents' },
  { value: 'restrict', label: 'Restrict: block the delete' },
  { value: 'setNull', label: 'Set null' },
  { value: 'noAction', label: 'No action' },
  { value: 'clientCascade', label: 'Cascade in EF only' },
  { value: 'clientSetNull', label: 'Set null in EF only' },
  { value: 'clientNoAction', label: 'No action in EF only' },
];

// ------------------------------------------------------------------ properties

function PropertyRows({ sketch, path, container, keys, canKey }: { sketch: SketchRecord; path: string; container: EntitySpec | OwnedSpec; keys: Set<string>; canKey: boolean }) {
  const id = sketch.id;
  const ref = sketchRef(sketch);
  const base = useBaseModel(sketch);
  const [focus, setFocus] = useState<{ name: string; nonce: number } | null>(null);
  const root = path.split('.')[0]!;
  const prefix = path.includes('.') ? `${path.slice(root.length + 1)}.` : '';
  const entityKey = (container as EntitySpec).key;

  const add = () => {
    let name = 'Property';
    for (let i = 2; container.properties[name] || container.owned?.[name] || container.complex?.[name]; i++) name = `Property${i}`;
    if (editSketch(id, (d) => addProperty(d, path, name))) setFocus({ name, nonce: Date.now() });
  };

  return (
    <div className="-mx-1">
      <TypeOptions />
      {Object.entries(container.properties).map(([name, p]) => {
        const isKey = keys.has(name);
        return (
          <div key={name} className="group flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-hover" data-property={name}>
            <button
              type="button"
              disabled={!canKey}
              title={canKey ? (isKey ? 'Remove from the primary key' : 'Add to the primary key') : isKey ? 'Key' : undefined}
              onClick={() => {
                const next = isKey ? (entityKey ?? []).filter((k) => k !== name) : [...(entityKey ?? []), name];
                editSketch(id, (d) => setKey(d, path, next));
              }}
              className={cn(
                'flex h-6 w-5 flex-none items-center justify-center rounded pointer-coarse:h-8 pointer-coarse:w-7',
                isKey ? 'text-changed' : 'text-transparent group-hover:text-faint',
                !isKey && canKey && 'pointer-coarse:text-faint/60',
                canKey && 'hover:bg-active',
              )}
            >
              <KeyRound size={11} />
            </button>
            <TextInput
              value={name}
              label={`Name of ${name}`}
              mono
              variant="inline" size="sm" width="flex-1"
              focusNonce={focus?.name === name ? focus.nonce : 0}
              onCommit={(to) => editSketch(id, (d) => renameProperty(d, path, name, to, base))}
            />
            <TextInput
              value={p.type}
              label={`Type of ${name}`}
              mono
              list="mv-types"
              variant="inline" size="sm" width="w-[88px] flex-none" muted
              onCommit={(type) => (type ? editSketch(id, (d) => updateProperty(d, path, name, { type })) : false)}
            />
            <button
              type="button"
              title={p.required ? 'Required. Click to allow null.' : 'Allows null. Click to require a value.'}
              aria-label={`${name} ${p.required ? 'is required' : 'allows null'}`}
              onClick={() => editSketch(id, (d) => updateProperty(d, path, name, { required: !p.required }))}
              className={cn('h-6 w-6 flex-none rounded font-mono text-[12px] hover:bg-active pointer-coarse:h-8 pointer-coarse:w-8', p.required ? 'text-faint' : 'text-changed')}
            >
              {p.required ? '!' : '?'}
            </button>
            <button
              type="button"
              title="More details"
              aria-label={`Details of ${name}`}
              onClick={() => select(ref, { kind: 'property', entity: root, property: `${prefix}${name}` })}
              className="flex h-6 w-6 flex-none items-center justify-center rounded text-faint hover:bg-active hover:text-text pointer-coarse:h-8 pointer-coarse:w-8"
            >
              <ArrowRight size={12} />
            </button>
            <button
              type="button"
              title={`Remove ${name}`}
              aria-label={`Remove ${name}`}
              onClick={() => editSketch(id, (d) => removeProperty(d, path, name))}
              className="flex h-6 w-6 flex-none items-center justify-center rounded text-faint opacity-0 group-hover:opacity-100 hover:bg-removed-soft hover:text-removed focus:opacity-100 pointer-coarse:h-8 pointer-coarse:w-8 pointer-coarse:opacity-100"
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
      <div className="mt-1.5 px-1">
        <ActionButton onClick={add}>
          <Plus size={13} /> Add property
        </ActionButton>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ entity

function EntityEditor({ sketch, entityKey }: { sketch: SketchRecord; entityKey: string }) {
  const id = sketch.id;
  const ref = sketchRef(sketch);
  const model = sketch.doc.model;
  const e = model.entities[entityKey]!;
  const base = useBaseModel(sketch);
  const focus = useNameFocus();
  const focusNonce = focus.key === entityKey ? focus.nonce : 0;
  const [relateTo, setRelateTo] = useState('');
  const [ownerChoice, setOwnerChoice] = useState('');
  const derived = Object.entries(model.entities).filter(([, x]) => x.base === entityKey).map(([k]) => k);
  // An entity cannot derive from itself or from anything that derives from it.
  const descendants = new Set<string>([entityKey]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const [k, x] of Object.entries(model.entities)) {
      if (x.base && descendants.has(x.base) && !descendants.has(k)) {
        descendants.add(k);
        grew = true;
      }
    }
  }
  const relationships = model.relationships.map((r, i) => [r, i] as const).filter(([r]) => r.dependent === entityKey || r.principal === entityKey || r.dependent.startsWith(`${entityKey}.`));
  const owners = model.relationships.filter((r) => r.dependent === entityKey && model.entities[r.principal] && r.principal !== entityKey).map((r) => r.principal);

  const rename = (to: string) => {
    if (!to || to === entityKey) return false;
    // Check now; apply once the card's position has moved to the new name, to the document as it is then.
    if (!attempt(id, (d) => renameEntity(d, entityKey, to, base))) return false;
    void moveLayoutEntry(id, entityKey, to).then(() => {
      if (editSketch(id, (d) => renameEntity(d, entityKey, to, base))) select(ref, { kind: 'entity', key: to });
    });
    return true;
  };

  return (
    <>
      <Header kind="Entity">
        <TextInput value={entityKey} label="Entity name" onCommit={rename} focusNonce={focusNonce} onFocused={() => useNameFocus.setState({ key: null })} size="lg" />
        {e.clrName && <p className="mt-1 truncate font-mono text-[11px] text-faint">{e.clrName}</p>}
      </Header>
      <Section title="Overview">
        <Row label="Table">
          <TextInput value={e.mapping?.table ?? ''} placeholder="by convention" label="Table" onCommit={(table) => editSketch(id, (d) => updateEntity(d, entityKey, { table: table || null }))} />
        </Row>
        <Row label="Derives from">
          <Select
            label="Base entity"
            value={e.base ?? ''}
            options={[{ value: '', label: 'Nothing (a root entity)' }, ...Object.keys(model.entities).filter((k) => !descendants.has(k)).map((k) => ({ value: k, label: k }))]}
            onChange={(b) => editSketch(id, (d) => updateEntity(d, entityKey, { base: b || null }))}
          />
        </Row>
        {!e.base && derived.length > 0 && (
          <Row label="Inheritance">
            <Select
              label="Inheritance mapping"
              value={e.mapping?.strategy ?? 'tph'}
              options={[
                { value: 'tph', label: 'One table for all (TPH)' },
                { value: 'tpt', label: 'A table per type (TPT)' },
                { value: 'tpc', label: 'A table per concrete type (TPC)' },
              ]}
              onChange={(strategy) => editSketch(id, (d) => updateEntity(d, entityKey, { strategy }))}
            />
          </Row>
        )}
        {!e.base && (
          <Row label="Key">
            <Check checked={!!e.keyless} label="No key (a view or query result)" onChange={(keyless) => editSketch(id, (d) => updateEntity(d, entityKey, { keyless }))} />
          </Row>
        )}
        <Row label="Comment">
          <TextInput value={e.comment ?? ''} label="Comment" onCommit={(comment) => editSketch(id, (d) => updateEntity(d, entityKey, { comment: comment || null }))} />
        </Row>
      </Section>
      <Section title="Properties" count={Object.keys(e.properties).length}>
        <PropertyRows sketch={sketch} path={entityKey} container={e} keys={new Set(e.key ?? [])} canKey={!e.base} />
      </Section>
      {(e.owned || e.complex) && (
        <Section title="Owned and complex types">
          {Object.entries(e.owned ?? {}).map(([nav, o]) => (
            <button key={nav} onClick={() => select(ref, { kind: 'entity', key: `${entityKey}.${nav}` })} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover">
              <Package size={13} className="text-faint" /> {nav}
              <span className="ml-auto font-mono text-[11px] text-muted">
                {o.type}
                {o.collection ? '[]' : ''}
              </span>
            </button>
          ))}
          {Object.entries(e.complex ?? {}).map(([name, c]) => (
            <div key={name} className="flex items-center gap-2 px-1.5 py-1 text-[12.5px]" title="Complex types are edited as JSON for now">
              <Braces size={13} className="text-faint" /> {name}
              <span className="ml-auto font-mono text-[11px] text-muted">{c.type}</span>
            </div>
          ))}
        </Section>
      )}
      <Section title="Relationships" count={relationships.length}>
        {relationships.map(([r, i]) => {
          const outgoing = r.dependent === entityKey || r.dependent.startsWith(`${entityKey}.`);
          return (
            <button key={i} onClick={() => select(ref, { kind: 'relationship', index: i })} className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover">
              <Link2 size={13} className="flex-none text-faint" />
              <span className="min-w-0 flex-1 truncate">{outgoing ? (r.dependentNavigation ?? r.foreignKey.join(', ')) : (r.principalNavigation ?? `(${r.dependent})`)}</span>
              <span className="flex-none text-[11px] text-faint">
                {outgoing ? '→' : '←'} {outgoing ? r.principal : r.dependent}
              </span>
            </button>
          );
        })}
        <div className="mt-1.5 flex items-center gap-1.5">
          <Select
            label="Relate to"
            value={relateTo}
            options={[{ value: '', label: `${entityKey} belongs to…` }, ...Object.keys(model.entities).map((k) => ({ value: k, label: k }))]}
            onChange={setRelateTo}
          />
          <ActionButton
            disabled={!relateTo}
            onClick={() => {
              if (editSketch(id, (d) => addRelationship(d, { dependent: entityKey, principal: relateTo }))) {
                setRelateTo('');
                select(ref, { kind: 'relationship', index: getSketch(id)!.doc.model.relationships.length - 1 });
              }
            }}
          >
            Add
          </ActionButton>
        </div>
        <p className="mt-1 text-[11.5px] text-faint">Or drag from the dot on the card to the entity it belongs to.</p>
      </Section>
      <Section title="Mentioned in">
        <div className="-mx-1.5">
          <MentionedIn target={`entity:sketch:${id}|${entityKey}`} />
        </div>
      </Section>
      <Section title="Actions">
        <div className="flex flex-wrap gap-1.5">
          {owners.length > 0 && (
            <>
              {owners.length > 1 && (
                <div className="w-full">
                  <Select label="Owner" value={ownerChoice || owners[0]!} options={owners.map((o) => ({ value: o, label: `Owned by ${o}` }))} onChange={setOwnerChoice} />
                </div>
              )}
              <ActionButton
                title="Store it inside its owner (OwnsOne or OwnsMany), without its own identity"
                onClick={() => {
                  const owner = ownerChoice || owners[0]!;
                  if (editSketch(id, (d) => makeOwned(d, entityKey, { owner }))) select(ref, { kind: 'entity', key: owner });
                }}
              >
                <Package size={13} /> Make owned by {ownerChoice || owners[0]}
              </ActionButton>
            </>
          )}
          <ActionButton danger onClick={() => editSketch(id, (d) => removeEntity(d, entityKey)) && select(ref, null)}>
            <Trash2 size={13} /> Delete entity
          </ActionButton>
        </div>
      </Section>
    </>
  );
}

function OwnedEditor({ sketch, path, spec }: { sketch: SketchRecord; path: string; spec: OwnedSpec }) {
  const ref = sketchRef(sketch);
  const owner = path.split('.')[0]!;
  return (
    <>
      <Header kind={spec.collection ? 'Owned collection' : 'Owned type'}>
        <h2 className="flex items-center gap-2 text-[14px] font-semibold">
          <Package size={15} className="text-faint" /> {path}
        </h2>
        <p className="mt-1 text-[12px] text-muted">
          {spec.type} stored inside{' '}
          <button className="text-accent hover:underline" onClick={() => select(ref, { kind: 'entity', key: owner })}>
            {owner}
          </button>
          {spec.mapping?.json ? `, as JSON in ${spec.mapping.json}` : ''}
        </p>
      </Header>
      <Section title="Properties" count={Object.keys(spec.properties).length}>
        <PropertyRows sketch={sketch} path={path} container={spec} keys={new Set(spec.key ?? spec.ownerKey)} canKey={false} />
      </Section>
    </>
  );
}

// ------------------------------------------------------------------ property

function PropertyEditor({ sketch, entity, rowId }: { sketch: SketchRecord; entity: string; rowId: string }) {
  const id = sketch.id;
  const ref = sketchRef(sketch);
  const base = useBaseModel(sketch);
  const parts = rowId.split('.');
  const name = parts.pop()!;
  const path = [entity, ...parts].join('.');
  const container = specAt(sketch.doc.model, path);
  const p = container?.properties[name];
  if (!container || !p) {
    return <p className="p-3 text-[12.5px] text-muted">{container ? 'This property no longer exists.' : 'Properties of complex types are edited as JSON for now.'}</p>;
  }
  const update = (patch: { [K in keyof PropertySpec]?: PropertySpec[K] | null }) => editSketch(id, (d) => updateProperty(d, path, name, patch));
  const text = p.type === 'string' || p.type === 'byte[]';
  const numeric = p.type === 'decimal' || p.type === 'double' || p.type === 'float';
  const isRoot = path === entity && !sketch.doc.model.entities[entity]?.base;
  const key = (container as EntitySpec).key ?? [];
  return (
    <>
      <Header kind="Property">
        <button className="mb-1 text-[12px] text-muted hover:text-text" onClick={() => select(ref, { kind: 'entity', key: path })}>
          {path}
        </button>
        <TextInput
          value={name}
          label="Property name"
          size="lg"
          onCommit={(to) => {
            if (!to || to === name) return false;
            if (!editSketch(id, (d) => renameProperty(d, path, name, to, base))) return false;
            select(ref, { kind: 'property', entity, property: [...parts, to].join('.') });
          }}
        />
        <p className="mt-1 font-mono text-[12px] text-muted">{typeLabel(p)}</p>
      </Header>
      <Section title="Details">
        <TypeOptions />
        <Row label="Type">
          <TextInput value={p.type} list="mv-types" mono label="Type" onCommit={(type) => (type ? update({ type }) : false)} />
        </Row>
        <Row label="Required">
          <Check checked={p.required} label={p.required ? 'Yes, never null' : 'No, allows null'} onChange={(required) => update({ required })} />
        </Row>
        {isRoot && (
          <Row label="Key">
            <Check
              checked={key.includes(name)}
              label="Part of the primary key"
              onChange={(on) => editSketch(id, (d) => setKey(d, entity, on ? [...key, name] : key.filter((k) => k !== name)))}
            />
          </Row>
        )}
        {(text || p.maxLength !== undefined) && (
          <Row label="Max length">
            <NumberInput value={p.maxLength} min={1} label="Max length" onCommit={(maxLength) => update({ maxLength })} />
          </Row>
        )}
        {(numeric || p.precision !== undefined) && (
          <>
            <Row label="Precision">
              <NumberInput value={p.precision} min={1} label="Precision" onCommit={(precision) => update({ precision })} />
            </Row>
            <Row label="Scale">
              <NumberInput value={p.scale} label="Scale" onCommit={(scale) => update({ scale })} />
            </Row>
          </>
        )}
        <Row label="Generated">
          <Select
            label="Generated"
            value={p.generated ?? ''}
            options={[
              { value: '', label: 'Never (set by the app)' },
              { value: 'onAdd', label: 'On insert' },
              { value: 'onUpdate', label: 'On update' },
              { value: 'onAddOrUpdate', label: 'On insert and update' },
            ]}
            onChange={(generated) => update({ generated: generated || null })}
          />
        </Row>
        <Row label="Concurrency">
          <Check checked={!!p.concurrencyToken} label="Concurrency token" onChange={(on) => update({ concurrencyToken: on || null })} />
        </Row>
        <Row label="Collection">
          <Check checked={!!p.collection} label="A list of values in one column" onChange={(on) => update({ collection: on || null })} />
        </Row>
        <Row label="Default">
          <TextInput
            value={p.default === undefined ? '' : JSON.stringify(p.default)}
            mono
            placeholder="none (JSON value)"
            label="Default value"
            onCommit={(v) => {
              if (!v) return update({ default: null });
              try {
                return update({ default: JSON.parse(v) });
              } catch {
                toast('The default must be a JSON value, for example 0, true or "text".', 'error');
                return false;
              }
            }}
          />
        </Row>
        <Row label="Comment">
          <TextInput value={p.comment ?? ''} label="Comment" onCommit={(comment) => update({ comment: comment || null })} />
        </Row>
      </Section>
      <div className="px-3 py-3">
        <ActionButton danger onClick={() => editSketch(id, (d) => removeProperty(d, path, name)) && select(ref, { kind: 'entity', key: path })}>
          <Trash2 size={13} /> Remove property
        </ActionButton>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ relationship

function RelationshipEditor({ sketch, index }: { sketch: SketchRecord; index: number }) {
  const id = sketch.id;
  const ref = sketchRef(sketch);
  const r = sketch.doc.model.relationships[index];
  if (!r) return <p className="p-3 text-[12.5px] text-muted">This relationship no longer exists.</p>;
  const update = (patch: Parameters<typeof updateRelationship>[2]) => editSketch(id, (d) => updateRelationship(d, index, patch));
  const rootOf = (p: string) => p.split('.')[0]!;
  return (
    <>
      <Header kind="Relationship">
        <h2 className="flex items-center gap-2 text-[14px] font-semibold">
          <button className="hover:underline" onClick={() => revealEntity(ref, rootOf(r.dependent))}>
            {r.dependent}
          </button>
          <ArrowRight size={14} className="text-faint" />
          <button className="hover:underline" onClick={() => revealEntity(ref, rootOf(r.principal))}>
            {r.principal}
          </button>
        </h2>
        <p className="mt-1 text-[12px] text-muted">
          Foreign key <span className="font-mono">{r.foreignKey.join(', ')}</span> on {r.dependent}
        </p>
      </Header>
      <Section title="Details">
        <Row label="Cardinality">
          <Select
            label="Cardinality"
            value={r.cardinality}
            options={[
              { value: 'one-to-many', label: `Many ${r.dependent} per ${r.principal}` },
              { value: 'one-to-one', label: `One ${r.dependent} per ${r.principal}` },
            ]}
            onChange={(cardinality) => update({ cardinality })}
          />
        </Row>
        <Row label="Required">
          <Check checked={r.required} label={`Every ${r.dependent} needs a ${r.principal}`} onChange={(required) => update({ required, ...(required && (r.onDelete === 'setNull' || r.onDelete === 'clientSetNull') ? { onDelete: 'cascade' as const } : {}) })} />
        </Row>
        {r.cardinality === 'one-to-one' && (
          <Row label="Dependent">
            <Check checked={!!r.requiredDependent} label={`Every ${r.principal} needs a ${r.dependent}`} onChange={(requiredDependent) => update({ requiredDependent })} />
          </Row>
        )}
        <Row label="On delete">
          <Select label="On delete" value={r.onDelete} options={DELETE_OPTIONS} onChange={(onDelete) => update({ onDelete })} />
        </Row>
        <Row label={`${r.dependent.split('.').pop()} →`}>
          <TextInput value={r.dependentNavigation ?? ''} mono placeholder="no navigation" label="Navigation to the principal" onCommit={(v) => update({ dependentNavigation: v || undefined })} />
        </Row>
        <Row label={`${r.principal.split('.').pop()} →`}>
          <TextInput value={r.principalNavigation ?? ''} mono placeholder="no navigation" label="Navigation to the dependents" onCommit={(v) => update({ principalNavigation: v || undefined })} />
        </Row>
      </Section>
      <div className="flex flex-wrap gap-1.5 px-3 py-3">
        <ActionButton danger onClick={() => editSketch(id, (d) => removeRelationship(d, index)) && select(ref, null)}>
          <Trash2 size={13} /> Remove relationship
        </ActionButton>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ sketch

export function SketchSummary({ sketch }: { sketch: SketchRecord }) {
  const id = sketch.id;
  const readOnly = !!sketch.origin;
  const ref = sketchRef(sketch);
  const doc = sketch.doc;
  const baseRef = baseRefOf(sketch);
  const baseModel = useBaseModel(sketch);
  const base = baseLabel(sketch);
  const findings = useMemo(() => lintModel(doc.model), [doc.model]);
  const diff = useMemo(() => (baseModel ? diffModels(baseModel, doc.model, { renames: doc.renames }) : null), [baseModel, doc]);
  const changes = diff?.changes ?? [];
  // Validation, plus declared renames that match nothing in the base model.
  const issues = useMemo(
    () => [
      ...validateDocument(doc).issues,
      ...(diff?.ignoredRenames ?? []).map((r) => ({
        severity: 'warning' as const,
        code: 'W_RENAME_UNUSED',
        path: 'renames',
        message: `The rename ${r.entity ? `${r.entity}: ` : ''}${r.from} → ${r.to} matches nothing in ${base ?? 'the base model'}.`,
        hint: r.from.includes('.') && !r.entity ? 'An owned type that becomes an entity (or back) cannot be declared as a rename.' : undefined,
      })),
    ],
    [doc, diff, base],
  );
  const setField = (field: 'title' | 'description' | 'useCase' | 'rationale', value: string) =>
    editSketch(id, (d) => {
      if (field === 'title') return value ? { ...d, title: value } : d;
      const next = { ...d };
      if (value) next[field] = value;
      else delete next[field];
      return next;
    });
  const entityOfPath = (path: string) => /^model\.entities\.([^.[]+)/.exec(path)?.[1];

  return (
    <>
      <Header kind={readOnly ? 'Sketch from the folder' : 'Sketch'}>
        {readOnly ? (
          <h2 className="text-[14px] font-semibold">{doc.title}</h2>
        ) : (
          <TextInput value={doc.title} label="Sketch title" size="lg" onCommit={(title) => (title ? setField('title', title) : false)} />
        )}
        {readOnly && (
          <button onClick={() => openSourceFile(sketch.origin!.path)} className="mt-1 block max-w-full truncate font-mono text-[11.5px] text-accent hover:underline">
            {sketch.origin!.path}
          </button>
        )}
        {base && baseRef && (
          <p className="mt-1.5 text-[12px] text-muted">
            Started from{' '}
            <button className="text-accent hover:underline" onClick={() => (baseRef.sketchId ? openSketch(baseRef.sketchId) : openDiagram(baseRef))}>
              {base}
            </button>
          </p>
        )}
      </Header>
      <Section title="About">
        {readOnly ? (
          <div className="flex flex-col gap-2 text-[12.5px]">
            {([['Use case', doc.useCase], ['Rationale', doc.rationale], ['Description', doc.description]] as const).map(([label, text]) =>
              text ? (
                <div key={label}>
                  <p className="text-[11.5px] text-faint">{label}</p>
                  <p className="whitespace-pre-wrap">{text}</p>
                </div>
              ) : null,
            )}
            {!doc.useCase && !doc.rationale && !doc.description && <p className="text-faint">No use case or rationale in the file.</p>}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <TextArea label="Use case" value={doc.useCase ?? ''} placeholder="The situation this design is for" onCommit={(v) => setField('useCase', v)} />
            <TextArea label="Rationale" value={doc.rationale ?? ''} placeholder="Why this design; trade-offs" rows={3} onCommit={(v) => setField('rationale', v)} />
            <TextArea label="Description" value={doc.description ?? ''} placeholder="Notes" onCommit={(v) => setField('description', v)} />
          </div>
        )}
      </Section>
      {baseModel && (
        <Section title="Changes" count={changes.length}>
          <div className="-mx-3">
            <ChangeList
              changes={changes}
              after={doc.model}
              before={baseModel}
              empty="No changes yet. Edit the diagram, the inspector or the JSON."
              onReveal={(key, removed) => (removed && baseRef ? revealEntity(baseRef, key) : revealEntity(ref, key))}
            />
          </div>
        </Section>
      )}
      <Section title="Review" count={findings.length}>
        {findings.length === 0 && <p className="text-[12px] text-faint">Nothing to point out.</p>}
        <ul data-testid="review-findings">
          {findings.map((f, i) => (
            <li key={i}>
              <button
                onClick={() => f.entity && revealEntity(ref, f.entity.split('.')[0]!)}
                className="flex w-full gap-2 rounded-md px-1.5 py-1 text-left text-[12px] hover:bg-hover"
                title={f.hint}
              >
                <span className={cn('mt-[5px] h-1.5 w-1.5 flex-none rounded-full', f.severity === 'warning' ? 'bg-changed' : 'bg-faint')} />
                <span className="min-w-0">
                  <span className="text-text">{f.message}</span>
                  {f.hint && <span className="block text-[11.5px] text-muted">{f.hint}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Section>
      {issues.length > 0 && (
        <Section title="Problems" count={issues.length}>
          <ul data-testid="sketch-problems">
            {issues.map((issue, i) => (
              <li key={i}>
                <button
                  onClick={() => {
                    const entity = entityOfPath(issue.path);
                    if (entity) revealEntity(ref, entity);
                  }}
                  className="flex w-full gap-2 rounded-md px-1.5 py-1 text-left text-[12px] hover:bg-hover"
                >
                  <span className={cn('mt-[5px] h-1.5 w-1.5 flex-none rounded-full', issue.severity === 'error' ? 'bg-removed' : 'bg-changed')} />
                  <span className="min-w-0">
                    <span className="text-text">{issue.message}</span>
                    {issue.hint && <span className="block text-[11.5px] text-muted">{issue.hint}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title="Actions">
        <div className="flex flex-wrap gap-1.5">
          {baseRef && (
            <ActionButton onClick={() => openCompare([baseRef, ref])}>
              <Columns2 size={13} /> Compare with {base ?? 'base'}
            </ActionButton>
          )}
          <ActionButton onClick={() => openCopyForAgent({ base: ref })}>
            <Bot size={13} /> Copy for agent
          </ActionButton>
          <ActionButton onClick={() => exportSketch(sketch)}>
            <Download size={13} /> Download
          </ActionButton>
          <ActionButton onClick={() => void duplicateSketch(id).then((copy) => copy && openSketch(copy.id))}>
            <Copy size={13} /> {readOnly ? 'Duplicate to edit' : 'Duplicate'}
          </ActionButton>
          {!readOnly && (
            <ActionButton
              danger
              onClick={() => {
                if (!window.confirm(`Delete the sketch "${doc.title}"? This cannot be undone.`)) return;
                closePanel(`sketch:${id}`);
                void deleteSketch(id);
              }}
            >
              <Trash2 size={13} /> Delete sketch
            </ActionButton>
          )}
        </div>
      </Section>
    </>
  );
}

export function SketchInspector({ sketch, value }: { sketch: SketchRecord; value: Selection | null }) {
  const ref = sketchRef(sketch);
  if (!value) return <SketchSummary sketch={sketch} />;
  const back = (
    <button onClick={() => select(ref, null)} className="flex h-8 w-full items-center gap-1.5 border-b border-border px-3 text-[12px] text-muted hover:bg-hover hover:text-text">
      <ArrowLeft size={13} /> {sketch.doc.title}
    </button>
  );
  const model = sketch.doc.model;
  let body: React.ReactNode;
  if (value.kind === 'entity') {
    if (model.entities[value.key]) body = <EntityEditor key={value.key} sketch={sketch} entityKey={value.key} />;
    else {
      const owned = specAt(model, value.key);
      body = owned ? <OwnedEditor sketch={sketch} path={value.key} spec={owned as OwnedSpec} /> : <p className="p-3 text-[12.5px] text-muted">Not in this sketch.</p>;
    }
  } else if (value.kind === 'property') {
    body = <PropertyEditor key={`${value.entity}::${value.property}`} sketch={sketch} entity={value.entity} rowId={value.property} />;
  } else {
    body = <RelationshipEditor key={value.index} sketch={sketch} index={value.index} />;
  }
  return (
    <>
      {back}
      {body}
    </>
  );
}
